import { useEffect, useMemo, useState, useRef } from 'react'
import { POLL } from '@/lib/pollTiers'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { adminAppUrl } from '@/lib/appUrls'
import { type SupabaseClient } from '@supabase/supabase-js'
import { useSaveDb } from '@/lib/useSaveDb'
import { signOutEverywhere } from '@/lib/offlineSession'
import { useRealtimeRefresh } from '@/lib/realtime'
import {
  AlertTriangle, CheckCircle, Send, Wrench, Play,
  Clock, ShieldCheck, Package, Building2, Sprout, ChevronDown, Server,
  FlaskConical, Ship, Boxes, Sparkles, Loader2, Store, Truck, Citrus, GraduationCap,
  type LucideIcon,
} from 'lucide-react'
import { usePersistedState } from '@/hooks/usePersistedState'
import { pulseExportLimitLabel, type AgeSummary } from '@/lib/aging'
import { useCoolingLane } from '@/hooks/useCoolingLane'
import { useTenantFlows } from '@/lib/entityFlow'
import clsx from 'clsx'
import { useResumeLine } from '@/modules/packhouse/maintenance/hooks'
import { confirmDialog } from '@/components/Dialogs'
import MutationOverlays from '@/components/MutationOverlays'
import { HelpBarButton } from '@/components/HelpBarButton'
import { TalkToSupportButton } from '@/components/TalkToSupportButton'
import { StatTile, AlertPill } from './DashboardTiles'
import { CoolingPill, activeCoolingBatches } from './CoolingPill'
import { AlertFold, splitAlerts, type AlertItem, type AlertTone } from './AlertFold'
import { LineDownStatus } from './LineDownStatus'
import { useRememberedWindowScroll } from '@/hooks/useRememberedWindowScroll'
import OfflinePrepButton from '@/components/OfflinePrepButton'
import NotificationBell from '@/components/NotificationBell'
import PackoutTrendChart from '@/components/PackoutTrendChart'
import { MODULES_BY_CATEGORY, hasModule, FARM_TENANT_PACKHOUSE_MODULES, PAYROLL_TENANT_MODULES, PLATFORM_TENANT_MODULES } from '@/lib/modules'
import { useFlowPipeline } from '@/hooks/useCommodityTemplates'
import { useCurrentUser } from '@/lib/useCurrentUser'
import { usePermissions } from '@/lib/usePermissions'
import { useCanFixPalletBilling } from '@/hooks/useCanFixPalletBilling'
import { unbilledPalletsOf, showsUnbilledAlert } from '@/lib/inventoryPulse'
import { useModuleGate } from '@/hooks/useCanOpenModule'
import FarmTodayStrip from './FarmTodayStrip'
import { claimHomeViewDefault, mustClampToFarm } from './homeViewMemory'
import { COMPLIANCE_RENEWALS_PATH, INSPECTION_WITH_PPECB_PATH, YARD_PATH, COLDROOM_PATH, COLDROOM_AGING_PATH, COLDROOM_CRITICAL_PATH, LOADING_BAY_PATH, tapIfOpenable } from './dashboardAlertLink'
import { PALLETS_REJECTED_PATH } from '@/modules/packhouse/usePalletsStatusLink'
import { RUNS_ACTIVE_ALL_HISTORY_PATH, RUNS_AWAITING_VERIFY_PATH } from '@/lib/runsRegister'
import { useActiveTenant, type TenantKind } from '@/lib/useActiveTenant'
import { offersTraining } from '@/lib/trainingGuides'
import { useIsPlatformAdmin } from '@/lib/useIsPlatformAdmin'
import { isAdmin, isManagerOrAbove, canSubscribeAddons, offersOfflinePrep } from '@/lib/roles'
import FarmerPulse from '@/components/FarmerPulse'
import BigFinanceOnlyNotice from '@/components/BigFinanceOnlyNotice'
import FirstRunChecklist from '@/components/FirstRunChecklist'
import { alertDialog } from '@/components/Dialogs'
import HelpButton from '@/components/HelpButton'
import TenantSwitchOverlay from '@/components/TenantSwitchOverlay'
import AccountMenu from '@/components/AccountMenu'
import { withTimeout, withReadTimeout, DASHBOARD_WIDGET_TIMEOUT_MS } from '@/lib/saveTimeout'
import WidgetRetry from '@/components/WidgetRetry'
import { refreshSessionAfterMetadataChange } from '@/lib/authRefresh'
import { useNavigateWithReturn } from '@/lib/returnTo'
import { OPEN_MISSION_STATUSES } from '@/lib/missionStatus'

// Tiles are now driven by the central module registry — see src/lib/modules.ts.
// Per-user `enabled_modules` filters the visible tiles below.

// SAST midnight today → UTC ISO for timestamp comparisons.
function sastMidnightISO() {
  const now   = new Date(Date.now() + 2 * 60 * 60 * 1000)
  const ymd   = now.toISOString().slice(0, 10)
  return new Date(`${ymd}T00:00:00+02:00`).toISOString()
}

interface LocationAging {
  coldroom:    AgeSummary
  loadingBay:  AgeSummary
}

interface InventoryPulse {
  // Right-now
  binsInYard:                number
  activeRuns:                number
  palletsInColdroom:         number
  palletsAwaitingInspection: number

  // Today-scoped
  palletsPackedToday:        number
  palletsDispatchedToday:    number
  rejectKgToday:             number
  wasteKgToday:              number
  todayPackoutPct:           number | null
  binsTippedToday:           number

  // Attention
  runsAwaitingVerification:  number
  missionsInProgress:        number
  consignmentsPending:       number
  // null = the server withheld it: this seat does not keep Unbilled pallets
  // (mig 20261066000000, F09-NARROW). Never coerced to 0 — see inventoryPulse.
  unbilledPallets:           number | null
  palletsRejected:           number
  complianceOverdue:         number   // expired items needing immediate action
  complianceDueSoon:         number   // < 7 days
  rejectBinsReady:           number   // closed reject bins awaiting disposition

  // Aging (split by location)
  agingByLocation:           LocationAging
}

// Single round-trip — server-side aggregation via mig 312 RPC. Replaces the
// 18-query fan-out that silently capped at 1000 rows on the aging + unbilled
// lookups; now every count is computed in Postgres and the wire payload is
// one JSONB row.
// Reads through the passed client. The Dashboard hands it the isolated
// useSaveDb() client so this widget can't be wedged behind the main client's
// in-process auth-token lock (supabase-js resolves the bearer via getSession()
// for every request and serialises those reads; a slow/black-holed refresh
// queues every rpc() behind it for up to the ~30s recovery ceiling — longer
// than the 15s widget timeout, so the widget would time out mid-wedge). The
// isolated client bakes a static bearer and never calls getSession per request,
// so it bypasses that lock. RPC is security_invoker → RLS scopes by
// active_tenant_id, which the isolated client's token carries.
async function fetchInventoryPulse(db: SupabaseClient): Promise<InventoryPulse> {
  // A read, not a save (UX-035 family): no slow_save/save_timeout telemetry.
  const { data, error } = await withReadTimeout(db.rpc('get_inventory_pulse', {
    p_today_start: sastMidnightISO(),
  }), 'Dashboard: get_inventory_pulse', DASHBOARD_WIDGET_TIMEOUT_MS)
  if (error) throw error
  // A NULL from the rpc reaches this cast intact — `as` asserts, it does not
  // check — and the very next line reads a key off it. The pulse tiles already
  // have <WidgetRetry/> behind `pulseError`; throwing shows that instead of
  // crashing the widget. Not `data ?? {}`: every field here is coerced with
  // `?? 0`, so an empty object would state "0 bins in yard" as fact when the
  // truth is that we could not find out.
  if (data == null) throw new Error('get_inventory_pulse returned no data')
  // The RPC returns numeric for sums; coerce to JS number so reducers stay
  // tidy downstream. JSONB → JS preserves keys 1:1 with the SQL builder.
  const j = data as Record<string, any>
  const num = (k: string) => Number(j[k] ?? 0)
  return {
    binsInYard:                num('binsInYard'),
    activeRuns:                num('activeRuns'),
    palletsInColdroom:         num('palletsInColdroom'),
    palletsAwaitingInspection: num('palletsAwaitingInspection'),

    palletsPackedToday:        num('palletsPackedToday'),
    palletsDispatchedToday:    num('palletsDispatchedToday'),
    rejectKgToday:             num('rejectKgToday'),
    wasteKgToday:              num('wasteKgToday'),
    todayPackoutPct:           j.todayPackoutPct != null ? Number(j.todayPackoutPct) : null,
    binsTippedToday:           num('binsTippedToday'),

    runsAwaitingVerification:  num('runsAwaitingVerification'),
    missionsInProgress:        num('missionsInProgress'),
    consignmentsPending:       num('consignmentsPending'),
    unbilledPallets:           unbilledPalletsOf(j),
    palletsRejected:           num('palletsRejected'),
    complianceOverdue:         num('complianceOverdue'),
    complianceDueSoon:         num('complianceDueSoon'),
    rejectBinsReady:           num('rejectBinsReady'),

    agingByLocation: {
      coldroom:   j.agingByLocation?.coldroom   as AgeSummary,
      loadingBay: j.agingByLocation?.loadingBay as AgeSummary,
    },
  }
}

// Helper — build a named URL destination for each alert.
function alertDestination(kind: 'runs' | 'pallets' | 'dispatch' | 'inspection'): string {
  switch (kind) {
    case 'runs':         return '/runs'
    case 'pallets':      return '/pallets'
    case 'dispatch':     return '/dispatch'
    case 'inspection':   return '/inspection'
  }
}

// ---------------------------------------------------------------------------
// Small components
// ---------------------------------------------------------------------------
function AgingHeatMap({
  title, summary, onClick,
}: {
  /** Omitted for a reader who can't open the page (F16 UF-05): the bar
   *  still reads, it just isn't a button that bounces them back to Home. */
  title: string; summary: AgeSummary; onClick?: () => void
}) {
  if (summary.total === 0) return null
  const Shell = onClick ? 'button' : 'div'
  return (
    <Shell
      onClick={onClick}
      className={clsx('surface rounded-2xl p-3 text-left w-full', onClick && 'active:scale-[0.99] transition-transform')}
    >
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs text-fg-muted uppercase tracking-wider">{title}</p>
        <p className="text-xs text-fg-muted">{summary.total} active</p>
      </div>
      <div className="flex gap-1.5 h-5 rounded-full overflow-hidden surface-raised">
        {(['ok', 'warning', 'critical', 'over'] as const).map(band => {
          const count = summary[band]
          if (count === 0) return null
          const pct = (count / summary.total) * 100
          const colour =
            band === 'ok'       ? 'bg-green-600'  :
            band === 'warning'  ? 'bg-amber-500'  :
            band === 'critical' ? 'bg-red-600'    :
                                  'bg-red-800'
          return (
            <div
              key={band}
              className={clsx(
                'flex items-center justify-center text-xs font-bold text-white',
                colour,
                (band === 'critical' || band === 'over') && 'animate-pulse'
              )}
              style={{ width: `${pct}%` }}
              title={`${count} ${band}`}
            >
              {pct > 12 ? count : ''}
            </div>
          )
        })}
      </div>
      <div className="grid grid-cols-4 gap-1.5 mt-1.5 text-center">
        {(['ok', 'warning', 'critical', 'over'] as const).map(band => (
          <div key={band} className="text-xs">
            <p className={clsx(
              'font-bold tabular-nums',
              band === 'ok'       && 'text-green-400',
              band === 'warning'  && 'text-amber-400',
              band === 'critical' && 'text-red-400',
              band === 'over'     && 'text-red-300',
            )}>{summary[band]}</p>
            <p className="text-fg-muted uppercase tracking-wider">{band}</p>
          </div>
        ))}
      </div>
    </Shell>
  )
}

// ---------------------------------------------------------------------------
// Hook: subscribe to Postgres changes that affect the dashboard.
// Each relevant table invalidates the supervisor-stats query so tiles update
// within seconds of the underlying data changing.
// ---------------------------------------------------------------------------
function useDashboardRealtime() {
  const qc = useQueryClient()
  // Scope the subscription to THIS tenant: a tenant-suffixed channel + a
  // tenant_id filter on every watch. Postgres-changes runs a per-subscriber
  // RLS check on a single thread, so an unfiltered whole-table watch makes
  // every tenant's Dashboard socket pay for every other tenant's row churn
  // (scale audit F2). Gate on tid so we never subscribe with `eq.undefined`.
  const tid = useActiveTenant().data?.tenantId
  useRealtimeRefresh(`dashboard-pulse:${tid ?? 'none'}`, [
    { table: 'pallets',            filter: `tenant_id=eq.${tid}` },
    { table: 'runs',               filter: `tenant_id=eq.${tid}` },
    { table: 'deliveries',         filter: `tenant_id=eq.${tid}` },
    { table: 'reject_log',         filter: `tenant_id=eq.${tid}` },
    { table: 'loading_missions',   filter: `tenant_id=eq.${tid}` },
    { table: 'ppecb_consignments', filter: `tenant_id=eq.${tid}` },
  ], () => {
    qc.invalidateQueries({ queryKey: ['inventory-pulse'] })
    // The Home tile's "which one truck" read goes stale on the same changes.
    qc.invalidateQueries({ queryKey: ['dashboard-only-open-mission'] })
  }, !!tid, 1500)
}

// ---------------------------------------------------------------------------
// Main page — FR27 Inventory Pulse
// ---------------------------------------------------------------------------
// Pre-existing complexity: cyclomatic 73, cognitive 71 — critical BEFORE this
// change, which adds two branches (the PAYROLL category gate and its module
// allowlist) to a function that already had 73. The gate re-attributes a
// touched function's inherited complexity as introduced, so it surfaces here
// rather than being new. Untangling a 73-branch page component is not
// something to attempt inside a tenant-kind addition; REFACTOR-BACKLOG owns
// the Dashboard split.
// fallow-ignore-next-line complexity
export default function DashboardPage() {
  const navigate = useNavigate()
  useDashboardRealtime()

  // Filter tiles by the user's enabled modules. Admins see everything;
  // other roles use their explicit list, falling back to role defaults.
  // Custom-role holders (mig 20260706150000) resolve from their
  // permission map instead — passed into hasModule below.
  // Group by category so the dashboard reads as Intake → Production →
  // Quality → … rather than 26 colour blocks in a row.
  const { data: me, isPending: mePending } = useCurrentUser()
  const { perms } = usePermissions()
  // Platform admins get a discoverable door to the console (WALL 1 fix — the
  // surface was reachable only by typed URL). UI convenience only; every
  // platform page re-gates server-side.
  const isPlatformAdmin = useIsPlatformAdmin()
  const platformUrl = adminAppUrl('/')

  // App context — packhouse vs farm. Switched via the hamburger menu in
  // the header. Filters the module sections so each mode only shows its
  // own work surfaces. Routes are still reachable directly by URL — this
  // is a dashboard-level visual scope, not a routing gate.
  const [appMode, setAppMode] = usePersistedState<'packhouse' | 'farm'>('kf49.appMode', 'packhouse')
  const [menuOpen, setMenuOpen] = useState(false)

  // Active tenant — drives the dashboard shape for FARM-kind tenants
  // (external farmers who pack at KF49). Packhouse mode then renders the
  // slim "My Fruit at KF49" panel instead of the full Inventory Pulse,
  // and module tiles are filtered to the cross-tenant-readable set.
  const { data: tenant } = useActiveTenant()
  const isFarmer        = tenant?.kind === 'FARM'
  const isPackhouseKind = tenant?.kind === 'PACKHOUSE'
  // A PAYROLL tenant is a system of record, not a site — see
  // PAYROLL_TENANT_MODULES. Its people clock and do piece-work at the SITE
  // tenants via secondment, so the operational tiles would all be dead ends.
  const isPayrollKind   = tenant?.kind === 'PAYROLL'
  // BigAgri's own billing tenant: one module (SaaS billing), see PLATFORM_TENANT_MODULES.
  const isPlatformKind  = tenant?.kind === 'PLATFORM'
  const isAgent         = tenant?.kind === 'AGENT'
  const isSupplier      = tenant?.kind === 'SUPPLIER'
  const isExporter      = tenant?.kind === 'EXPORTER'
  const isTransporter   = tenant?.kind === 'TRANSPORTER'
  const isHarvest       = tenant?.kind === 'HARVEST'
  const isSecurity      = tenant?.kind === 'SECURITY'
  const flows           = useTenantFlows()
  // Berry cooling line (F16 UF-10): only a packhouse whose flows force-cool,
  // and only once the flow read has answered (a failed read is not "citrus").
  const showCooling     = isPackhouseKind && flows.anyForceCools && !flows.flowsUnread
  const coolingLane     = useCoolingLane({ enabled: showCooling })
  const pipeline        = useFlowPipeline()     // packhouse show/hide + within-band order overlay (mig 879)

  // "My Fruit at KF49" on a FARM tenant is the cross-tenant peek into the
  // packhouse's intake/run data for the farmer's own fruit. Admin-only —
  // foremen/scouts/workers stay on the farm-side surface.
  const canSeePackhouseView = isAdmin(me?.role)

  // Default the view the FIRST time Home sees a business in this page's life:
  // FARM tenants land on 'farm' (own orchard), PACKHOUSE on 'packhouse'. The
  // memory is module-level (homeViewMemory.ts), not a per-mount ref: a return
  // to Home — a bounce from a page included — is not a business switch, so it
  // keeps "My fruit". A reload, the next day or a switch still lands on My Farm.
  useEffect(() => {
    const id = tenant?.tenantId ?? null
    if (!id || !claimHomeViewDefault(id)) return
    if (tenant?.kind === 'FARM')      setAppMode('farm')
    if (tenant?.kind === 'PACKHOUSE') setAppMode('packhouse')
  }, [tenant?.tenantId, tenant?.kind, setAppMode])

  // BUYER tenants' home is the BigTrade buyer surface (shipments +
  // Marketplace entry live at /buyer; buyer_* modules are reached from there).
  useEffect(() => {
    if (tenant?.kind === 'BUYER') navigate('/buyer', { replace: true })
  }, [tenant?.kind, navigate])

  // Packhouses this FARM tenant is linked to (via grower_tenant_links).
  // Empty = independent farm with no cross-tenant peek (e.g. Magalies
  // provisioned 2026-05-20). Used to gate the "My fruit at …" view
  // toggle below: no links → no toggle, the "My fruit at KF49" label
  // was a stale hardcode that didn't make sense for unlinked farms.
  const { data: linkedPackhouses = [], isPending: linksPending } = useQuery<Array<{ tenant_id: string; display_name: string }>>({
    queryKey: ['my-linked-packhouses', tenant?.tenantId],
    enabled:  isFarmer,
    queryFn: async () => {
      // Path is: this farm tenant's growers (RLS scopes to my own farm tenant
      // PLUS cross-tenant linked-farmer rows at packhouses) → each grower
      // carries its tenant_id (post-mig-535) → tenants, filtered to kind
      // PACKHOUSE so the farm's own growers (tenant_id = my farm) drop out
      // and only the linked packhouses remain. Two explicit fetches per the
      // cross-tenant nested-embed rule in the playbook.
      const { data: growers, error: gErr } = await supabase
        .from('growers')
        .select('tenant_id')
      if (gErr) throw gErr

      const ids = Array.from(new Set(
        (growers ?? []).map(g => (g as { tenant_id: string }).tenant_id).filter(Boolean),
      ))
      if (ids.length === 0) return []

      const { data: tenants, error: tErr } = await supabase
        .from('tenants')
        .select('tenant_id, display_name')
        .eq('kind', 'PACKHOUSE')
        .in('tenant_id', ids)
      if (tErr) throw tErr
      return (tenants ?? []) as Array<{ tenant_id: string; display_name: string }>
    },
  })

  // Veg gating: the veg cost tiles (Utilities / Field Ops / Seed) only make
  // sense once a farm grows veg. Signal = the tenant has ≥1 non-orchard
  // (PIVOT/FIELD/OTHER) block. A mixed citrus+veg farm has one, so it sees the
  // veg tiles AND keeps its orchard tools; a pure-citrus farm (all ORCHARD)
  // never sees them. RLS scopes the count to this farm tenant's own blocks.
  const { data: hasVegBlocks = false } = useQuery({
    queryKey: ['farm-has-veg-blocks', tenant?.tenantId],
    enabled:  isFarmer,
    queryFn: async () => {
      const { count, error } = await supabase
        .from('blocks')
        .select('block_id', { count: 'exact', head: true })
        .neq('land_type', 'ORCHARD')
        .is('deleted_at', null)
      if (error) throw error
      return (count ?? 0) > 0
    },
  })

  // Mirror: orchard-only tools (Phenology — tree growth stages) are hidden from
  // a pure-veg farm (no ORCHARD block). A mixed farm has one, so it keeps them.
  const { data: hasOrchardBlocks = false, isSuccess: orchardKnown } = useQuery({
    queryKey: ['farm-has-orchard-blocks', tenant?.tenantId],
    enabled:  isFarmer,
    queryFn: async () => {
      const { count, error } = await supabase
        .from('blocks')
        .select('block_id', { count: 'exact', head: true })
        .eq('land_type', 'ORCHARD')
        .is('deleted_at', null)
      if (error) throw error
      return (count ?? 0) > 0
    },
  })
  const hasPackhouseLink = linkedPackhouses.length > 0

  // Clamp persisted appMode: if a non-admin (or unlinked) FARM tenant lands
  // with a stale 'packhouse' choice, force them back to the farm surface
  // they're permitted to see. Unlinked farms have no cross-tenant peek at
  // all, so packhouse mode would render an empty Inventory Pulse panel.
  // Held until `me` and the links have both ANSWERED (homeViewMemory.ts): the
  // `[]` default and an unloaded user read as "not allowed" for a beat, and
  // clamped an admin's chosen view to My Farm.
  const clampToFarm = mustClampToFarm({
    isFarmer, appMode,
    meAnswered: !mePending, linksAnswered: !linksPending,
    canSeePackhouseView, hasPackhouseLink,
  })
  useEffect(() => {
    if (clampToFarm) setAppMode('farm')
  }, [clampToFarm, setAppMode])

  const visibleSections = useMemo(() => {
    const isFarmCat     = (key: string) => key.startsWith('farm_')
    const isAgentCat    = (key: string) => key.startsWith('agent_')
    const isSupplierCat = (key: string) => key.startsWith('supplier_')
    const isExporterCat = (key: string) => key.startsWith('exporter_')
    const isTransporterCat = (key: string) => key.startsWith('transporter_')
    const isHarvestCat  = (key: string) => key.startsWith('harvest_')
    // 'security_' CATEGORY prefix only: the Phase 1 keys 'security' /
    // 'farm_security' live in the ops / farm_admin categories.
    const isSecurityCat = (key: string) => key.startsWith('security_')
    // Veg-only farm tiles — hidden until the farm has a non-orchard block.
    // Utilities is NOT here: an orchard farm pays for its pumps too, and the
    // Budget reads those invoices on every farm.
    const VEG_ONLY_FARM_MODULES = new Set(['farm_field_ops', 'farm_seed'])
    // Orchard-only farm tiles — hidden from a pure-veg farm (no orchard block).
    const ORCHARD_ONLY_FARM_MODULES = new Set(['phenology'])
    // Read once — the per-tile filter below is the hot closure.
    const tenantMods = tenant?.enabledModules ?? null
    const tenantSlug = tenant?.slug
    return MODULES_BY_CATEGORY
      // Filtering rule per tenant kind:
      //   * PACKHOUSE tenants: every category EXCEPT farm_*, agent_*, supplier_*, exporter_*.
      //   * FARM tenants: keep the toggle. farm mode → farm_* only,
      //     packhouse mode → non-farm, non-agent, non-supplier, non-exporter ("My Fruit at KF49").
      //   * AGENT tenants: agent_* only.
      //   * SUPPLIER tenants: supplier_* only.
      //   * EXPORTER tenants: exporter_* only (BigExport).
      //   * TRANSPORTER tenants: transporter_* only (BigLogistics).
      //   * HARVEST tenants: harvest_* only (BigHarvest) — category keys, so
      //     the farm module KEY 'harvest_picks' (category farm_*) is unaffected.
      //   * SECURITY tenants: security_* only (BigSecurity).
      // NOTE the packhouse/farm arms are NEGATIVE lists, so every new kind's
      // family must be excluded here too or its tiles surface on a packhouse
      // dashboard whose tenant has no explicit enabled_modules.
      //
      // One-arm-per-kind chain: adding PAYROLL takes it from 23 branches to 24.
      // It reads as a lookup table but cannot become one — the packhouse and
      // farm arms are negative lists, per the NOTE above — and this gate
      // decides which tiles EVERY tenant kind sees, so restructuring it inside
      // a tenant-kind addition would risk far more than it tidies.
      // fallow-ignore-next-line complexity
      .filter(s => {
        const k = s.category.key
        // The billing category belongs to the PLATFORM kind alone, and that kind
        // sees nothing else (also stops a persisted appMode 'farm' leaking farm tiles).
        if (k === 'platform_billing') return isPlatformKind
        if (isPlatformKind)  return false
        if (isExporter)      return isExporterCat(k)
        if (isSupplier)      return isSupplierCat(k)
        if (isTransporter)   return isTransporterCat(k)
        if (isHarvest)       return isHarvestCat(k)
        if (isSecurity)      return isSecurityCat(k)
        if (isAgent)         return isAgentCat(k)
        if (isPayrollKind)   return !isFarmCat(k) && !isAgentCat(k) && !isSupplierCat(k) && !isExporterCat(k) && !isTransporterCat(k) && !isHarvestCat(k) && !isSecurityCat(k)
        if (isPackhouseKind) return !isFarmCat(k) && !isAgentCat(k) && !isSupplierCat(k) && !isExporterCat(k) && !isTransporterCat(k) && !isHarvestCat(k) && !isSecurityCat(k)
        // FARM tenant
        return appMode === 'farm'
          ? isFarmCat(k)
          : (!isFarmCat(k) && !isAgentCat(k) && !isSupplierCat(k) && !isExporterCat(k) && !isTransporterCat(k) && !isHarvestCat(k) && !isSecurityCat(k))
      })
      .map(s => {
        let modules = s.modules.filter(m => hasModule(me ?? null, m.key, tenantMods, perms, tenantSlug))
        // Veg cost tiles only appear once the farm has a non-orchard block
        // (handles mixed citrus+veg: such a farm has one, so it sees them).
        if (isFarmer && !hasVegBlocks) {
          modules = modules.filter(m => !VEG_ONLY_FARM_MODULES.has(m.key))
        }
        // Only once the read has answered: while it is pending or failed (a
        // cold or offline open) Growth Stages stays on Home.
        if (isFarmer && orchardKnown && !hasOrchardBlocks) {
          modules = modules.filter(m => !ORCHARD_ONLY_FARM_MODULES.has(m.key))
        }
        if (isFarmer && appMode === 'packhouse') {
          modules = modules.filter(m => FARM_TENANT_PACKHOUSE_MODULES.includes(m.key))
        }
        // Same narrowing the ⌘K palette applies (navModulesFor). Without it a
        // PAYROLL tenant's dashboard shows the whole packhouse floor while the
        // palette shows three modules — two surfaces disagreeing about what the
        // tenant can even do.
        if (isPayrollKind) {
          modules = modules.filter(m => PAYROLL_TENANT_MODULES.includes(m.key))
        }
        if (isPlatformKind) {
          modules = modules.filter(m => PLATFORM_TENANT_MODULES.includes(m.key))
        }
        // Per-flow pipeline overlay (mig 879): hide the processes this flow
        // drops, then re-rank within the band by optional sort_order. Stable
        // sort → modules without an order keep their natural array position, so
        // with no sort_order rows the order is byte-identical to before. For
        // citrus this reproduces the old BLUEBERRY_ONLY_MODULES=Set(['precool'])
        // subtraction exactly (the overlay's CITRUS hidden-set is {'precool'}).
        if (isPackhouseKind) {
          modules = modules.filter(m => !pipeline.hiddenSet.has(m.key))
          modules = [...modules].sort(
            (a, b) => (pipeline.orderMap.get(a.key) ?? Number.MAX_SAFE_INTEGER)
                    - (pipeline.orderMap.get(b.key) ?? Number.MAX_SAFE_INTEGER),
          )
        }
        // Franchise admin tile only renders for FRANCHISE_ADMIN-roled users
        // (gFranchise gate redirects everyone else, so leaving the tile
        // visible would be a dead click).
        if (me?.role !== 'FRANCHISE_ADMIN') {
          modules = modules.filter(m => m.key !== 'franchise_admin')
        }
        return { ...s, modules }
      })
      .filter(s => s.modules.length > 0)
  }, [me, perms, appMode, isFarmer, isPackhouseKind, isAgent, isSupplier, isExporter, isSecurity, isPlatformKind, pipeline, tenant?.enabledModules, tenant?.slug, hasVegBlocks, hasOrchardBlocks, orchardKnown])

  // Inventory Pulse + other packhouse-specific widgets render for any
  // PACKHOUSE-kind tenant (toggle is gone for them — always shown).
  // FARM tenants get FarmerPulse instead in their packhouse view.
  const isPackhouse     = isPackhouseKind
  const showFarmerPulse = isFarmer && appMode === 'packhouse'

  // Coming back to Home — the bottom bar's Home tab, Back, the rail's Home —
  // lands where you left it, not at the top (W-14 / OR-03).
  const rootRef = useRef<HTMLDivElement>(null)
  useRememberedWindowScroll(
    tenant?.tenantId ? `home:${tenant.tenantId}:${appMode}` : null,
    visibleSections.length > 0, rootRef,
  )

  // Isolated lock-free client for the contended widget reads below — see
  // fetchInventoryPulse. null during cold boot → callers fall back to `supabase`.
  const pulseDb = useSaveDb()

  // Keys carry the tenant id: the persisted query cache otherwise restores the
  // PREVIOUS tenant's rows under a global key right after a tenant switch.
  const {
    data: stats,
    isError:    pulseError,
    isFetching: pulseFetching,
    isLoading:  pulseLoading,
    refetch:    pulseRefetch,
  } = useQuery({
    queryKey: ['inventory-pulse', tenant?.tenantId],
    // Isolated client (lock-free); falls back to the shared client during the
    // cold-boot window before useSaveDb captures a claim-bearing token.
    queryFn: () => fetchInventoryPulse(pulseDb ?? supabase),
    // 5min backstop only — the tenant-scoped dashboard-pulse realtime (F2) now
    // refreshes this within ~1.5s of any change, so the poll is just a catch-up
    // for a silently-dropped socket (rejoin-refetch + wake-heal cover the rest).
    // Scale audit F3. (The downtime poll below stays 30s — not realtime-covered.)
    refetchInterval: POLL.passive,
    enabled: isPackhouse,
  })

  // The Home tile opens THE truck when only one is open (ui-flow F04 UF-10),
  // and Dispatch's Today view otherwise. Read only when the pulse says one —
  // the same open set get_inventory_pulse counts. Anything but exactly one row
  // back (a second truck opened since, a failed read) falls back to Today.
  const navigateWithReturn = useNavigateWithReturn()
  const { data: onlyOpenMission } = useQuery({
    queryKey: ['dashboard-only-open-mission', tenant?.tenantId],
    enabled: isPackhouse && stats?.missionsInProgress === 1,
    queryFn: async () => {
      const { data, error } = await supabase.from('loading_missions')
        .select('mission_id, mission_number')
        .in('status', [...OPEN_MISSION_STATUSES])
        .is('deleted_at', null)
        .limit(2)
      if (error) throw error
      const rows = (data ?? []) as { mission_id: string; mission_number: string }[]
      return rows.length === 1 ? rows[0] : null
    },
  })
  const theOpenMission = stats?.missionsInProgress === 1 ? onlyOpenMission ?? null : null

  // Currently-open downtime — drives a hero alert pill so everyone on the
  // floor sees that the line is stopped without opening the Downtime page.
  const { data: openDowntime, isError: downtimeError } = useQuery({
    queryKey: ['dashboard-open-downtime', tenant?.tenantId],
    refetchInterval: POLL.live,
    enabled: isPackhouse && !!tenant?.tenantId,
    queryFn: async () => {
      const { data, error } = await supabase.from('downtime_log')
        .select('downtime_id, reason, started_at')
        .eq('tenant_id', tenant!.tenantId).is('ended_at', null).order('started_at', { ascending: false })
        .limit(1).maybeSingle()
      if (error) throw error
      return data as { downtime_id: string; reason: string; started_at: string } | null
    },
  })


  // F16 UF-13: Resume line beside the red pill, for whoever the route gate lets
  // into Maintenance (the server lets any member of the business write the stop).
  const resumeLine = useResumeLine()
  const canResumeLine = hasModule(me ?? null, 'maintenance', tenant?.enabledModules ?? null, perms, tenant?.slug)
  async function askResumeLine(id: string, startedAt: string) {
    const mins = Math.max(0, Math.floor((Date.now() - new Date(startedAt).getTime()) / 60_000))
    const ok = await confirmDialog({
      title: 'Resume the line?',
      message: `Resume the line now? The stop ends at ${mins} min. This cannot be undone.`,
      confirm: 'Resume line',
      cancel: 'Keep it stopped',
    })
    if (ok) resumeLine.mutate(id)
  }

  async function signOut() {
    await signOutEverywhere()
  }

  // Unbilled pallets follows the pallet Edit rule (F09 R1): its alert is for
  // whoever may open the page. The server gives the figure to the same seats
  // only (can_fix_pallet_billing(), F09-NARROW) and null to every other one,
  // so a refused seat shows no alert and no number, never an inflated count.
  const canFixBilling = useCanFixPalletBilling() === true
  // The compliance pills open Renewals — for whoever the route gate lets in.
  const canOpenModule = useModuleGate()
  const openRenewals = tapIfOpenable(canOpenModule('compliance'), () => navigate(COMPLIANCE_RENEWALS_PATH))
  // The PPECB pill opens Inspection · With PPECB (UF-04) — dispatchers can't
  // open Inspection, so for them it reads as text.
  const openWithPpecb = tapIfOpenable(canOpenModule('inspection'), () => navigate(INSPECTION_WITH_PPECB_PATH))
  // The Right-now tiles and the coldroom bar open what they count (UF-05);
  // for a reader the route gate would refuse, they stay figures.
  const openYard       = tapIfOpenable(canOpenModule('yard'), () => navigate(YARD_PATH))
  const openActiveRuns = tapIfOpenable(canOpenModule('runs'), () => navigate(RUNS_ACTIVE_ALL_HISTORY_PATH))
  const openRejectBins = tapIfOpenable(canOpenModule('reject_bins'), () => navigate('/reject-bins'))
  const openAwaitingVerify = tapIfOpenable(canOpenModule('runs'), () => navigate(RUNS_AWAITING_VERIFY_PATH))
  const openColdroom   = tapIfOpenable(canOpenModule('coldroom'), () => navigate(COLDROOM_PATH))
  const openColdroomAging = tapIfOpenable(canOpenModule('coldroom'), () => navigate(COLDROOM_AGING_PATH))

  // Attention alerts that only appear when count > 0
  const alerts: AlertItem[] = []
  const pushAlert = (key: string, open: boolean | null, node: React.ReactNode, shows = true, tone?: AlertTone) => {
    alerts.push({ key, node, open, shows, tone })
  }
  // Pallet alerts open the filtered view they count (F09 UF-15): text for a reader the route gate would refuse.
  const openRejectedPallets = tapIfOpenable(canOpenModule('pallets'), () => navigate(PALLETS_REJECTED_PATH))
  const openColdroomCritical = tapIfOpenable(canOpenModule('coldroom'), () => navigate(COLDROOM_CRITICAL_PATH))
  const openLoadingBay = tapIfOpenable(canOpenModule('loading_bay'), () => navigate(LOADING_BAY_PATH))
  const canOpenMaintenance = canOpenModule('maintenance')
  if (openDowntime) {
    const minutes = Math.floor((Date.now() - new Date(openDowntime.started_at).getTime()) / 60_000)
    // The pill opens Maintenance only for a reader the route gate lets in; for
    // everyone else it is text plus a status line (F16 UF-01). Line down never folds.
    const pill = (
      <AlertPill
        key="line-down"
        icon={Wrench}
        count={minutes}
        label={`min line down · ${openDowntime.reason.replace(/_/g, ' ').toLowerCase()}`}
        tone="red"
        onClick={tapIfOpenable(canOpenMaintenance, () => navigate('/maintenance?tab=downtime'))}
      />
    )
    pushAlert('line-down', true, canResumeLine ? (
      <div key="line-down" className="flex items-stretch gap-2 min-w-0">
        <div className="flex-1 min-w-0 flex [&>button]:flex-1 [&>button]:min-w-0">{pill}</div>
        <button
          type="button"
          onClick={() => void askResumeLine(openDowntime.downtime_id, openDowntime.started_at)}
          disabled={resumeLine.isPending}
          className="shrink-0 min-h-[44px] rounded-xl px-3 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold inline-flex items-center gap-1 disabled:opacity-60"
        >
          <Play size={14} /> Resume line
        </button>
      </div>
    ) : (
      <div key="line-down" className="min-w-0 flex flex-col gap-1">
        {pill}
        {canOpenMaintenance === false && <LineDownStatus />}
      </div>
    ), true, 'red')
  } else if (downtimeError) {
    // A failed read is not "the line is running": say the status is unknown.
    pushAlert('line-down', true, (
      <AlertPill key="line-down" icon={Wrench} count="—" label="line status · couldn’t load" tone="red" />
    ), true, 'red')
  }
  if (showCooling) {
    pushAlert('cooling', canOpenModule('precool'), 
      <CoolingPill
        key="cooling"
        read={coolingLane.read}
        summary={coolingLane.summary}
        onClick={tapIfOpenable(canOpenModule('precool'), () => navigate('/precool'))}
      />,
      coolingLane.read !== 'usable' || activeCoolingBatches(coolingLane.summary) > 0,
      coolingLane.read === 'usable' && coolingLane.summary.overSla > 0 ? 'red' : 'sky',
    )
  }
  if (stats) {
    if (stats.agingByLocation.loadingBay.critical + stats.agingByLocation.loadingBay.over > 0) {
      pushAlert('loading-aging', canOpenModule('loading_bay'), 
        <AlertPill
          key="loading-aging"
          icon={Clock}
          count={stats.agingByLocation.loadingBay.critical + stats.agingByLocation.loadingBay.over}
          label={`loading-bay pallets near/over ${pulseExportLimitLabel(flows.flowsUnread ? [] : flows.enabledKeys)}`}
          tone="red"
          onClick={openLoadingBay}
        />, true, 'red'
      )
    }
    if (stats.agingByLocation.coldroom.critical + stats.agingByLocation.coldroom.over > 0) {
      pushAlert('coldroom-aging', canOpenModule('coldroom'), 
        <AlertPill
          key="coldroom-aging"
          icon={Clock}
          count={stats.agingByLocation.coldroom.critical + stats.agingByLocation.coldroom.over}
          label="coldroom pallets near/over limit"
          tone="amber"
          onClick={openColdroomCritical}
        />, true, 'amber'
      )
    }
    if (stats.palletsRejected > 0) {
      pushAlert('rejected', canOpenModule('pallets'), 
        <AlertPill
          key="rejected"
          icon={AlertTriangle}
          count={stats.palletsRejected}
          label="pallets rejected — repack needed"
          tone="red"
          onClick={openRejectedPallets}
        />, true, 'red'
      )
    }
    if (stats.runsAwaitingVerification > 0) {
      pushAlert('verify', canOpenModule('runs'), 
        <AlertPill
          key="verify"
          icon={ShieldCheck}
          count={stats.runsAwaitingVerification}
          label="runs awaiting Manager verify"
          tone="blue"
          onClick={openAwaitingVerify}
        />, true, 'blue'
      )
    }
    if (stats.complianceOverdue > 0) {
      pushAlert('compliance-overdue', canOpenModule('compliance'), 
        <AlertPill
          key="compliance-overdue"
          icon={ShieldCheck}
          count={stats.complianceOverdue}
          label="compliance items expired"
          tone="red"
          onClick={openRenewals}
        />, true, 'red'
      )
    }
    if (stats.complianceDueSoon > 0) {
      pushAlert('compliance-soon', canOpenModule('compliance'), 
        <AlertPill
          key="compliance-soon"
          icon={ShieldCheck}
          count={stats.complianceDueSoon}
          label="compliance items due in < 7 days"
          tone="amber"
          onClick={openRenewals}
        />, true, 'amber'
      )
    }
    if (stats.rejectBinsReady > 0) {
      pushAlert('reject-bins-ready', canOpenModule('reject_bins'), 
        <AlertPill
          key="reject-bins-ready"
          icon={Package}
          count={stats.rejectBinsReady}
          label="reject bins awaiting disposition"
          tone="blue"
          onClick={openRejectBins}
        />, true, 'blue'
      )
    }
    if (stats.consignmentsPending > 0) {
      pushAlert('consignments', canOpenModule('inspection'), 
        <AlertPill
          key="consignments"
          icon={Send}
          count={stats.consignmentsPending}
          label="PPECB consignments awaiting result"
          tone="sky"
          onClick={openWithPpecb}
        />, true, 'sky'
      )
    }
    if (showsUnbilledAlert(stats.unbilledPallets, canFixBilling)) {
      pushAlert('unbilled', canOpenModule('pallets'), 
        <AlertPill
          key="unbilled"
          icon={AlertTriangle}
          count={stats.unbilledPallets}
          label="pallets need a billing code"
          tone="amber"
          onClick={tapIfOpenable(canOpenModule('pallets'), () => navigate('/pallets/uncoded'))}
        />, true, 'amber'
      )
    }
  }

  const alertSplit = splitAlerts(alerts)

  return (
    <div ref={rootRef} className="min-h-full surface-sunk p-4 flex flex-col">
      {/* Header */}
      {/* flex-wrap + the icon cluster's own row: on one row the 7-button
          cluster crushed the tenant switcher to ~22px, under the ⌘K trigger.
          The cluster keeps its own row until lg, not sm: between 640 and ~900
          (every iPad in portrait) the pill and the cluster were both shrink-0
          on one row, and the pill sat on top of "Search / Go to" — a tap on
          search opened the tenant menu (OR-01 / W-11). From lg they share a
          row and the PILL gives way (it truncates), never overlaps. */}
      <header className="flex flex-wrap lg:flex-nowrap items-center justify-between mb-4 gap-3">
        {/* w-full below lg: the pill gets the whole first row instead of
            truncating beside empty space on a phone (OR-06). */}
        <div className="flex items-center gap-2 min-w-0 w-full lg:w-auto">
          <TenantContextSwitcher
            open={menuOpen}
            setOpen={setMenuOpen}
            appMode={appMode}
            setAppMode={setAppMode}
            tenantName={tenant?.displayName ?? null}
            tenantKind={tenant?.kind ?? null}
            isFarmer={isFarmer}
            canSeePackhouseView={canSeePackhouseView}
            linkedPackhouses={linkedPackhouses}
          />
          {/* One line that truncates: squeezed on one row it used to wrap a
              word a line, the build stamp with it (the stamp is in the footer
              now). Hidden from lg to xl, where the pill shares its row with
              the icon cluster and there is only room for the tenant name. */}
          <div className="min-w-0 hidden sm:block lg:hidden xl:block">
            <p className="text-sm text-fg-muted flex items-center gap-1.5 whitespace-nowrap">
              <span className="shrink-0 inline-block w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              <span className="truncate">
                {appMode === 'farm'
                  ? 'Field operations'
                  : isFarmer ? 'Live · packhouse view' : 'Inventory Pulse · live'}
              </span>
            </p>
          </div>
        </div>
        {/* Add-ons / Platform / Account keep their words from md, not sm:
            at 640–767 the labelled cluster (~670px) wrapped onto a third
            header row. */}
        <div className="order-last basis-full lg:order-none lg:basis-auto flex flex-wrap items-center justify-end gap-2 shrink-0">
          <HelpBarButton />
          <TalkToSupportButton />
          {/* On a packhouse only admins, managers and the Piece Work Capture
              station use what it prepares (UF-18); farms and every other kind
              keep it for all. */}
          {offersOfflinePrep(tenant?.kind, isManagerOrAbove(me?.role) || hasModule(me ?? null, 'packer_piecework', tenant?.enabledModules ?? null, perms, tenant?.slug)) && (
            <OfflinePrepButton />
          )}
          <NotificationBell />
          {/* Only for the people the Add-ons page lets subscribe (UF-17);
              everyone else could only read "Ask an admin" there. */}
          {canSubscribeAddons(me?.role) && (
            <button aria-label="Add-ons" onClick={() => navigate('/addons')} className="btn-ghost btn-sm" title="Add-ons & subscriptions">
              <Sparkles size={16} />
              <span className="hidden md:inline">Add-ons</span>
            </button>
          )}
          {/* The platform console lives in the admin app (admin.bigagri.app), a
              separate origin with its own sign-in, so this is a link out. */}
          {isPlatformAdmin && platformUrl && (
            <a href={platformUrl} target="_blank" rel="noopener noreferrer" aria-label="Platform"
              className="btn-ghost btn-sm" title="Platform console (admin app)">
              <Server size={16} className="text-purple-400" />
              <span className="hidden md:inline">Platform</span>
            </a>
          )}
          <HelpButton />
          <AccountMenu onSignOut={signOut} />
        </div>
      </header>

      {/* A BigAccounting-only login (an outside accountant, no membership) has
          nothing here: one line names where BigAccounting lives. */}
      <BigFinanceOnlyNotice />

      {/* FARM tenant in packhouse mode → slim "My fruit at KF49" panel.
          Cross-tenant SELECT (mig 233/234) auto-scopes the queries inside
          to the farmer's linked grower, no extra filters needed here. */}
      {showFarmerPulse && (
        <div className="mb-4">
          <FarmerPulse packhouses={linkedPackhouses} />
        </div>
      )}

      {/* Packhouse Inventory Pulse — alerts, KPIs, aging, missions. The farm
          app will get its own dashboard surface later; for now farm mode just
          shows the filtered tile grid below. */}
      {isPackhouse && (
        <>
          {/* First-run setup checklist — admins and managers of a
              packhouse with no pallet yet see it until every required step
              is done. It gates itself (role + first pallet) and deep-links
              each step to its exact section. */}
          <FirstRunChecklist />

          {/* Attention strip — only rendered when there's something to act on */}
          {alertSplit.kept.length + alertSplit.folded.length > 0 && (
            <div className="grid grid-cols-1 gap-2 mb-3 sm:grid-cols-2">
              {alertSplit.kept.map(a => a.node)}
              <AlertFold folded={alertSplit.folded} />
            </div>
          )}
          <MutationOverlays of={[[resumeLine, 'Line resumed']]} />

          {/* Right-now KPIs. The retry/loading affordance covers the whole
              pulse (one RPC feeds every tile below) — on a timeout the tiles
              keep their last-known values and the operator refreshes in place
              instead of reloading the app (UX-007). */}
          <div className="flex items-center justify-between gap-2 mb-1.5 px-1">
            <p className="text-xs font-semibold text-fg-muted uppercase tracking-wider">Right now</p>
            {pulseError ? (
              <WidgetRetry stale={!!stats} busy={pulseFetching} onRetry={() => { void pulseRefetch() }} />
            ) : pulseLoading ? (
              <span className="inline-flex items-center gap-1.5 text-[11px] text-fg-muted">
                <Loader2 size={12} className="animate-spin" /> Loading…
              </span>
            ) : null}
          </div>
          <div className="grid grid-cols-2 gap-2 mb-3 sm:grid-cols-4">
            <StatTile label="Bins in yard"    value={stats?.binsInYard ?? '—'}                sub="awaiting tipping" onClick={openYard} />
            <StatTile label="Active runs"     value={stats?.activeRuns ?? '—'}                                          onClick={openActiveRuns} />
            <StatTile label="In coldroom"     value={stats?.palletsInColdroom ?? '—'}         sub="pallets"          onClick={openColdroom} />
            {/* Opens Inspection for whoever may open it (F03 UF-03) — the
                tile named the queue and led nowhere. */}
            <StatTile label="Awaiting PPECB"  value={stats?.palletsAwaitingInspection ?? '—'} sub="loading area"
              onClick={hasModule(me ?? null, 'inspection', tenant?.enabledModules ?? null, perms, tenant?.slug)
                ? () => navigate(alertDestination('inspection'))
                : undefined} />
          </div>

          {/* Today-scoped KPIs */}
          <p className="text-xs font-semibold text-fg-muted uppercase tracking-wider mb-1.5 px-1">Today</p>
          <div className="grid grid-cols-2 gap-2 mb-3 sm:grid-cols-6">
            <StatTile label="Pallets packed"   value={stats?.palletsPackedToday ?? '—'} />
            <StatTile label="Dispatched"       value={stats?.palletsDispatchedToday ?? '—'} />
            <StatTile
              label="Packout"
              value={stats?.todayPackoutPct != null ? `${stats.todayPackoutPct.toFixed(1)}%` : '—'}
              accent={
                stats?.todayPackoutPct == null             ? undefined     :
                stats.todayPackoutPct < 85                 ? 'text-amber-400' :
                                                             'text-green-400'
              }
            />
            <StatTile label="Rejects"          value={stats ? `${stats.rejectKgToday.toFixed(0)} kg` : '—'}
              sub="sellable cull"
              accent="text-red-400" />
            <StatTile label="Waste"            value={stats ? `${stats.wasteKgToday.toFixed(0)} kg` : '—'}
              sub="disposed"
              accent="text-fg-muted" />
            <StatTile
              label="Bins tipped"
              value={stats?.binsTippedToday ?? '—'}
              sub="since midnight"
              accent="text-cyan-300"
            />
          </div>

          {/* 30-day packout trend */}
          <PackoutTrendChart />

          {/* Aging heat-maps — side-by-side when populated */}
          {stats && (stats.agingByLocation.coldroom.total > 0 || stats.agingByLocation.loadingBay.total > 0) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
              <AgingHeatMap
                title="Coldroom aging"
                summary={stats.agingByLocation.coldroom}
                onClick={openColdroomAging}
              />
              <AgingHeatMap
                title="Loading bay aging"
                summary={stats.agingByLocation.loadingBay}
                onClick={() => navigate('/loading-bay')}
              />
            </div>
          )}

          {/* Dispatch / mission tile */}
          {stats && stats.missionsInProgress > 0 && (
            <button
              onClick={() => theOpenMission
                ? navigateWithReturn(`/dispatch?mission=${theOpenMission.mission_id}`)
                : navigate('/dispatch')}
              className="bg-rose-950/40 border border-rose-800 rounded-2xl px-4 py-3 mb-4 text-left active:scale-[0.99] transition-transform flex items-center gap-3"
            >
              <Send size={18} className="text-rose-300 shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-rose-200">{stats.missionsInProgress} loading mission{stats.missionsInProgress === 1 ? '' : 's'} in progress</p>
                <p className="text-xs text-rose-300/80">
                  {theOpenMission
                    ? `Tap to open ${theOpenMission.mission_number}`
                    : "Tap to see today's trucks"}
                </p>
              </div>
              <CheckCircle size={14} className="text-rose-400/60" />
            </button>
          )}
        </>
      )}

      {/* The FARM Home's Today strip (FM11 UF-01) — My Farm view only; every
          other tenant kind's Home is untouched. */}
      {isFarmer && appMode === 'farm' && <FarmTodayStrip />}

      {/* Nav tiles — grouped by category, each section labelled. Filtered
          by per-user module access; empty sections are hidden. */}
      <div className="space-y-5 flex-1">
        {visibleSections.map(section => (
          <TileSection key={section.category.key} label={section.category.label}>
            {section.modules.map(({ label, icon, path, color }) => (
              <NavTile key={path} label={label} icon={icon} color={color} onClick={() => navigate(path)} />
            ))}
          </TileSection>
        ))}
        {/* Training is not a module: no permission key, so no role, custom
            role or entitlement list can hide it — every user of these apps
            gets it, even with no other tile yet. /training is ungated. */}
        {offersTraining(tenant?.kind) && (
          <TileSection label="Training">
            <NavTile label="Training" icon={GraduationCap} color="bg-sky-700" onClick={() => navigate('/training')} />
          </TileSection>
        )}
      </div>

      {/* POPIA footer — discoverable link to the privacy policy + the
          Subject Access Request page. Required by POPIA s18: the data
          subject must be able to find the responsible party's notice
          without already knowing the URL. */}
      <footer className="mt-auto pt-6 pb-3 px-2 text-center text-[11px] text-fg-muted space-x-3">
        <a href="/privacy" className="hover:text-fg-secondary">Privacy Policy</a>
        <span className="text-fg-muted">·</span>
        <a href="/privacy/my-data" className="hover:text-fg-secondary">My data</a>
        {/* Moved from the header, where it wrapped and fought the tenant pill
            for room; support still reads it here ("what does the foot of
            Home say?"). */}
        {import.meta.env.VITE_BUILD_STAMP && (
          <>
            <span className="text-fg-muted">·</span>
            <span className="text-fg-muted font-mono">build {import.meta.env.VITE_BUILD_STAMP}</span>
          </>
        )}
      </footer>
    </div>
  )
}

function TileSection({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="text-[11px] uppercase tracking-wider text-fg-muted font-bold mb-2 px-1">
        {label}
      </h2>
      {/* Denser on a touch tablet (OR-02 / X-07): 1024 landscape showed 10
          of 37 farm tiles, 3 columns of 124px. Four columns from lg, five
          from xl, and a shorter tile under touch from md. Below md —
          the phone — nothing changes. */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 md:coarse:gap-3">
        {children}
      </div>
    </section>
  )
}

function NavTile({ label, icon: Icon, color, onClick }: {
  label: string; icon: LucideIcon; color: string; onClick: () => void
}) {
  return (
    // A quiet graphite card; the group colour lives only in the icon badge
    // (the same pattern as Training's tiles), so Home still reads banded by
    // group while status colours stay the loudest thing on screen
    // (docs/specs/DESIGN-SYSTEM.md, "Signature element").
    <button
      type="button"
      onClick={onClick}
      className="bg-surface border border-line-subtle rounded-xl p-5 md:coarse:p-4 flex flex-col items-center justify-center gap-3 md:coarse:gap-2
                 hover:bg-raised active:scale-95 transition"
    >
      <span className={`${color} rounded-xl p-3 md:coarse:p-2.5`}>
        <Icon size={28} aria-hidden="true" className="text-on-fill md:coarse:w-6 md:coarse:h-6" />
      </span>
      <span className="text-callout font-semibold text-fg">{label}</span>
    </button>
  )
}

// ---------------------------------------------------------------------------
// TenantContextSwitcher — top-left pill showing the active tenant name +
// kind icon. Click to expand a dropdown that combines:
//   * Tenant switcher (mig 278) — every tenant the user is a member of,
//     with the active one highlighted. Click switches via
//     set_preferred_tenant + refreshSession + reload.
//   * App-mode toggle within the active tenant — packhouse view vs farm
//     view (or "My Fruit at KF49" vs "My Farm" for farm-tenant members).
//
// Replaces the old AppModeMenu hamburger which buried the tenant
// switcher and made the active-tenant context invisible. The pill is
// the single source-of-truth for "where am I and how do I switch".
// ---------------------------------------------------------------------------
// Pre-existing large component (pill + dropdown + switcher + mode toggle);
// this PR only adds the pending/paused/error rows the idle-wake audit
// demanded, but the new-only gate flags any edit to an already-over-threshold
// function. Splitting the switcher out of Dashboard is a separate refactor.
// fallow-ignore-next-line complexity
function TenantContextSwitcher({
  open, setOpen, appMode, setAppMode, tenantName, tenantKind, isFarmer,
  canSeePackhouseView, linkedPackhouses,
}: {
  open:                boolean
  setOpen:             (v: boolean) => void
  appMode:             'packhouse' | 'farm'
  setAppMode:          (v: 'packhouse' | 'farm') => void
  tenantName:          string | null
  // Was a hand-rolled copy of the TenantKind union, which is exactly how it
  // drifted when PAYROLL was added. Use the type.
  tenantKind:          TenantKind | null
  isFarmer:            boolean
  canSeePackhouseView: boolean
  // Packhouses this farm tenant has grower_tenant_links to. Empty array
  // = independent farm with no cross-tenant peek available.
  linkedPackhouses:    Array<{ tenant_id: string; display_name: string }>
}) {
  const ref = useRef<HTMLDivElement>(null)
  const qc  = useQueryClient()
  const hasPackhouseLink = linkedPackhouses.length > 0
  const packhouseLabel   = linkedPackhouses.length === 1
    ? linkedPackhouses[0].display_name
    : 'packhouses'

  // Close on outside click.
  useEffect(() => {
    if (!open) return
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open, setOpen])

  // Tenant switcher (mig 278). Only renders when the user has more than
  // one active membership — most users have exactly one and don't need
  // the noise. Switching writes preferred_tenant_id to app_metadata and
  // forces a JWT refresh; the page then reloads so every cached query
  // re-runs under the new active_tenant_id.
  type MyTenant = {
    tenant_id: string; slug: string; display_name: string; kind: string;
    status: string; role: string; is_preferred: boolean; is_active_now: boolean;
  }
  const {
    data: myTenants = [], isPending: tenantsPending, isFetching: tenantsFetching,
    isError: tenantsError, isPaused: tenantsPaused, refetch: refetchTenants,
  } = useQuery<MyTenant[]>({
    queryKey: ['my-tenants'],
    enabled:  open,           // only fetch when the dropdown opens
    // Treat data as always-stale. Combined with the invalidate-on-open
    // useEffect below, each open of the dropdown re-runs list_my_tenants
    // so memberships added to this user AFTER the dropdown was first
    // mounted appear without a page reload. Operators noticed this when
    // a freshly-provisioned tenant stayed invisible until F5
    // (report 2026-05-20).
    staleTime: 0,
    queryFn: async ({ signal }) => {
      // Interactive dropdown: 10s is generous for an indexed few-row RPC
      // (mig 278), and the default 30s ceiling reads as "broken" to an
      // operator staring at an open dropdown. On timeout ABORT the underlying
      // request — Promise.race alone leaves it running, so reopening the
      // dropdown stacked orphaned fetches (idle-wake audit, finding A6).
      // react-query's own signal (close/invalidate) composes via the listener.
      const ctrl = new AbortController()
      signal.addEventListener('abort', () => ctrl.abort(), { once: true })
      const { data, error } = await withReadTimeout(
        supabase.rpc('list_my_tenants').abortSignal(ctrl.signal),
        'Dashboard: list_my_tenants',
        10_000,
        () => ctrl.abort(),
      )
      if (error) throw error
      // BigFinance is deliberately isolated from the packhouse-app shell: it
      // has its own UI. BigAgri's own billing tenant (kind PLATFORM) IS shown
      // (Jason, 8 Oct 2026): it is a real tenant with one module, SaaS billing.
      // See: feedback_finance_app_isolated_no_switcher.md.
      return ((data ?? []) as MyTenant[]).filter(t => t.kind !== 'FINANCE')
    },
  })

  // Force a fresh fetch every time the dropdown opens. refetchOnMount
  // would only fire on the TenantContextSwitcher's initial mount, which
  // is once per page load — the switcher stays mounted across dropdown
  // toggles, so we invalidate explicitly here instead.
  // cancelRefetch: false — the default (true) CANCELLED the in-flight fetch
  // on every reopen and restarted its clock, so an operator toggling the
  // dropdown on a slow link could never reach data OR the error state.
  useEffect(() => {
    if (open) qc.invalidateQueries({ queryKey: ['my-tenants'] }, { cancelRefetch: false })
  }, [open, qc])

  const [switching, setSwitching] = useState<string | null>(null)
  // Captured at click time so the overlay label never depends on the
  // dropdown-only `my-tenants` query still holding data.
  const [switchingName, setSwitchingName] = useState<string | null>(null)

  // App-mode toggle (My Farm ↔ My fruit at <packhouse>) is a pure client-state
  // switch — no reload — but the dashboard re-lays-out + the new mode's queries
  // fire. Flash the same skeleton briefly so the change feels deliberate rather
  // than an abrupt tile-rearrange. Transient (auto-fades) since there's no
  // reload to span. The fade is keyed on a monotonic nonce (not the label) so a
  // toggle-out-and-back to the SAME label still gets its own fresh cycle.
  const [modeSwitch,  setModeSwitch]  = useState<string | null>(null)
  const [modeClosing, setModeClosing] = useState(false)
  const [modeNonce,   setModeNonce]   = useState(0)
  useEffect(() => {
    if (modeNonce === 0) return
    setModeClosing(false)
    const t1 = setTimeout(() => setModeClosing(true), 400)
    const t2 = setTimeout(() => { setModeSwitch(null); setModeClosing(false) }, 650)
    return () => { clearTimeout(t1); clearTimeout(t2) }
  }, [modeNonce])
  function switchMode(mode: 'packhouse' | 'farm', label: string) {
    // Never run while a tenant switch is in flight — the tenant overlay owns
    // the screen and an imminent reload would strand a just-written appMode.
    if (switching) return
    if (appMode === mode) { setOpen(false); return }
    setOpen(false)
    setAppMode(mode)
    setModeSwitch(label)
    setModeNonce(n => n + 1)
  }

  // Pre-existing large handler (the full tenant-switch sequence). This PR
  // REDUCES it — the private /token POST + manual persist moved into the shared
  // coordinator (authRefresh.ts) — but it stays over fallow's threshold, and the
  // new-only gate flags any edit to an already-over-threshold function. Suppress;
  // a deeper split of the switch flow is a separate refactor.
  // fallow-ignore-next-line complexity
  async function switchTo(tenantId: string, displayName?: string) {
    if (switching) return
    setSwitching(tenantId)
    setSwitchingName(displayName ?? null)
    // Outer-loop watchdog. If anything inside the try block hangs (an
    // RPC, a /token fetch, navigator.locks) we surface "took too long"
    // instead of leaving the operator staring at "switching…" forever.
    // 10s is generous — a healthy switch resolves in well under 1s.
    const watchdog = setTimeout(() => {
      void alertDialog({
        title:   'Tenant switch is taking too long',
        message: 'The browser may be stuck on a stale auth lock. Try refreshing the page; if that doesn\'t help, sign out and back in.',
        tone:    'warning',
      })
      setSwitching(null)
    }, 10_000)
    try {
      // 1. Write the new pin server-side. The auth hook (mig 454) reads
      // raw_app_meta_data.preferred_tenant_id whenever a new JWT is
      // minted, so the next /token call will carry the target claim.
      const { error } = await withTimeout(supabase.rpc('set_preferred_tenant', { p_tenant_id: tenantId }), 'Dashboard: set_preferred_tenant')
      if (error) throw error

      // 2. Mint a fresh JWT carrying the new active_tenant_id claim — through the
      // shared refresh coordinator (authRefresh.ts), NOT a private /token POST.
      // The switcher used to rotate the one-time refresh_token itself, racing the
      // watchdog / platform-view loop / auth-js; that interleaving replayed a
      // consumed token and revoked live admin sessions mid-switch (2026-06-15).
      // refreshSessionAfterMetadataChange waits out any in-flight refresh, then
      // runs ONE single-flight rotation that reflects the set_preferred_tenant
      // write above, persists it, and is rotation-guarded so it can never replay.
      const r = await refreshSessionAfterMetadataChange()
      if (!r.ok) {
        if (r.reason === 'revoked')    throw new Error('Your session has expired. Please sign out and sign back in.')
        if (r.reason === 'no-session') throw new Error('No active session — please sign in again.')
        throw new Error('Tenant switch took too long — refresh the page and try again.')
      }

      // 3. Verify the freshly-minted JWT carries the target claim. A mismatch
      // means set_preferred_tenant wrote but this token didn't reflect it (e.g.
      // we adopted a refresh that began before the write) — refusing to reload
      // protects against a silent no-op switch; the operator retries.
      let payload: any = null
      try {
        payload = JSON.parse(atob(r.session.access_token.split('.')[1]))
      } catch { /* fall through to mismatch error */ }
      if (payload?.active_tenant_id !== tenantId) {
        throw new Error('New session did not carry the target tenant claim. Refresh and try again.')
      }

      // 4. The coordinator already wrote the new session to localStorage and
      // synced it in-memory (best-effort), so the reload below boots a clean
      // client straight from it — no separate persist needed here.

      clearTimeout(watchdog)
      // Broadcast to other tabs that the active tenant has changed so they
      // reload before writing rows against the stale tenant_id (council T1.6).
      // Without this, Tab B keeps its 5-min cached tenantId AND its 1-hour
      // JWT - writes from Tab B silently cross-tenant for the next hour.
      const nm = displayName ?? myTenants.find(t => t.tenant_id === tenantId)?.display_name
      try {
        const bc = new BroadcastChannel('kf49.tenant-switch')
        // Carry the name so other tabs' boot overlay can show it too.
        bc.postMessage({ tenantId, name: nm ?? null, at: Date.now() })
        bc.close()
      } catch { /* old browser without BroadcastChannel - own-tab reload still wins */ }

      // Persist the switch overlay across the reload (sessionStorage survives a
      // same-tab reload) so the skeleton is continuous click → reload → boot,
      // never a flash of old data. App.tsx reads + clears this on boot.
      try {
        sessionStorage.setItem('kf49.switching', nm || '1')
      } catch { /* sessionStorage unavailable — overlay just won't span the reload */ }

      // Drop the previous tenant's cached API responses. The SW caches
      // /rest/v1 NetworkFirst keyed by URL (not tenant) — on a flaky network it
      // would otherwise serve the OLD tenant's rows for the new tenant (stale +
      // cross-tenant). Bounded so a stalled Cache API can never block the reload
      // (the whole z-200 overlay would otherwise stay up forever). Trade-off: on
      // a switch-then-immediately-offline this drops the old tenant's offline
      // window too — acceptable for the cross-tenant-isolation win.
      try {
        await Promise.race([
          (caches?.delete?.('supabase-api-cache') ?? Promise.resolve()).catch(() => {}),
          new Promise<void>(r => setTimeout(r, 1500)),
        ])
      } catch { /* best-effort */ }

      window.location.reload()
    } catch (e) {
      // Drop the z-200 overlay BEFORE the dialog — otherwise the opaque skeleton
      // covers the z-120 alert, the OK button is un-tappable, the await never
      // resolves, and the operator is hard-stuck (no Escape on mobile).
      clearTimeout(watchdog)
      setSwitching(null)
      setSwitchingName(null)
      await alertDialog({ title: 'Tenant switch failed', message: (e as Error).message, tone: 'danger' })
    }
  }

  // Pick the tenant icon + label colour based on kind for the trigger pill.
  const KindIcon = tenantKind === 'FARM'     ? Sprout
                 : tenantKind === 'PLATFORM' ? Server
                 : Building2
  const kindColor = tenantKind === 'FARM'     ? 'text-lime-400'
                  : tenantKind === 'PLATFORM' ? 'text-purple-400'
                  : 'text-blue-400'

  return (
    <>
      {/* Cover the whole app the instant a switch starts: hides the old
          tenant's data during the ~JWT-mint window, then a matching boot
          overlay (App.tsx) carries it across the reload. */}
      {switching && (
        <TenantSwitchOverlay name={switchingName} />
      )}
      {!switching && modeSwitch && (
        <TenantSwitchOverlay name={modeSwitch} closing={modeClosing} />
      )}
    {/* The -ml-2 sits on the wrapper, not the pill: on the pill it made the
        wrapper 8px narrower than the pill's own content, and `max-w-full`
        then truncated "E2E Packhouse" to "E2E Packho…" on a phone with 180px
        free beside it (OR-06). No shrink-0: at lg the pill shares a row with
        the icon cluster, and a pill that cannot shrink overlaps it (OR-01);
        this one truncates instead. */}
    <div ref={ref} className="relative min-w-0 -ml-2 sm:max-w-[18rem]">
      <button onClick={() => setOpen(!open)}
        title="Switch tenant or app view"
        className="flex items-center gap-2 px-2.5 py-2.5 min-h-[44px] rounded-xl hover:bg-surface text-fg border hairline-strong max-w-full">
        <KindIcon size={16} className={`${kindColor} shrink-0`} />
        <span className="font-bold truncate text-base">{tenantName ?? 'Loading…'}</span>
        <ChevronDown size={14} className="text-fg-muted shrink-0" />
      </button>
      {/* The panel caps to the viewport and scrolls itself. The pill sits ~4rem
          below the top of the page, so a user with many memberships (platform
          staff, multi-site groups) got a list that ran off the bottom of the
          screen with no way to reach its tail — an absolutely-positioned
          dropdown doesn't grow the page's own scroll. overscroll-contain stops
          a scroll at the list's edge from chaining through to the dashboard. */}
      {open && (
        <div className="absolute left-0 mt-1 z-dropdown surface border hairline-strong rounded-xl shadow-2xl min-w-[260px] max-w-[calc(100vw-2rem)] py-1 max-h-[calc(100dvh-7rem)] overflow-y-auto overscroll-contain">
          {/* Tenant switcher first — answer "where am I" before "what mode".
              Pending / paused / error MUST render something: for packhouse
              users this section is the whole dropdown, and it used to render
              an empty 260px box for all three (the "switcher shows nothing"
              idle-wake complaint — S1 in the 2026-07-02 audit). */}
          {(myTenants.length > 0 || tenantsPending || tenantsError) && (
            <>
              <p className="text-[10px] uppercase tracking-wider text-fg-muted px-3 py-1">
                {myTenants.length > 1 ? 'Your tenants' : 'Tenant'}
              </p>
              {myTenants.length === 0 && tenantsPaused && (
                <p className="px-3 py-2.5 text-sm text-fg-muted">
                  Offline — tenants will load when the connection returns
                </p>
              )}
              {myTenants.length === 0 && !tenantsPaused && tenantsPending && tenantsFetching && (
                <p className="px-3 py-2.5 text-sm text-fg-muted">Loading tenants…</p>
              )}
              {myTenants.length === 0 && !tenantsPaused && tenantsError && (
                <button
                  onClick={() => refetchTenants()}
                  className="w-full px-3 py-2.5 min-h-[44px] text-left text-sm text-amber-300 hover:bg-raised/40">
                  Couldn’t load tenants — tap to retry
                </button>
              )}
              {
                // One icon arm per tenant kind (a lookup written as JSX); adding
                // SECURITY took it over the threshold, not new logic.
                // fallow-ignore-next-line complexity
                myTenants.map(t => (
                <button
                  key={t.tenant_id}
                  onClick={() => { if (!t.is_active_now) switchTo(t.tenant_id, t.display_name) }}
                  disabled={t.is_active_now || !!switching}
                  className={`w-full flex items-center gap-2 px-3 py-2.5 min-h-[44px] text-left text-sm ${
                    t.is_active_now ? 'text-fg bg-raised/60' : 'text-fg-secondary hover:bg-raised/40'
                  } disabled:opacity-60`}
                  title={t.is_active_now ? 'Currently active tenant' : `Switch to ${t.display_name}`}>
                  {t.kind === 'PACKHOUSE'  && <Building2 size={14} className="text-blue-400 shrink-0" />}
                  {t.kind === 'FARM'       && <Sprout    size={14} className="text-lime-400 shrink-0" />}
                  {t.kind === 'AGENT'      && <FlaskConical size={14} className="text-sky-400 shrink-0" />}
                  {t.kind === 'EXPORTER'   && <Ship      size={14} className="text-teal-400 shrink-0" />}
                  {t.kind === 'SUPPLIER'   && <Boxes     size={14} className="text-amber-400 shrink-0" />}
                  {t.kind === 'BUYER'      && <Store     size={14} className="text-cyan-400 shrink-0" />}
                  {t.kind === 'TRANSPORTER' && <Truck    size={14} className="text-indigo-400 shrink-0" />}
                  {t.kind === 'HARVEST'    && <Citrus   size={14} className="text-orange-400 shrink-0" />}
                  {t.kind === 'SECURITY'   && <ShieldCheck size={14} className="text-fg-secondary shrink-0" />}
                  {t.kind === 'PLATFORM'   && <Server    size={14} className="text-purple-400 shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <p className="truncate">{t.display_name}</p>
                    <p className="text-[10px] text-fg-muted uppercase tracking-wider">{t.kind} · {t.role}</p>
                  </div>
                  {switching === t.tenant_id ? (
                    <span className="text-[10px] text-fg-muted">switching…</span>
                  ) : t.is_active_now ? (
                    <span className="text-[10px] text-emerald-400">active</span>
                  ) : null}
                </button>
              ))}
            </>
          )}

          {/* View toggle ONLY for FARM tenants WITH a packhouse link —
              they have two distinct surfaces (own orchard vs cross-tenant
              SELECT into their packhouse's data). Packhouse tenants get
              one unified view with all categories rendered together; the
              old "Farm" view for them was a misnomer.

              "My Fruit at <packhouse>" is admin-only — it's a cross-tenant
              peek into the packhouse's intake/run data, not a field
              operator's surface. Non-admin farm members only get "My Farm".
              Independent farms with no packhouse link (e.g. Magalies
              2026-05-20) skip the whole toggle since the cross-tenant peek
              would return nothing anyway. */}
          {isFarmer && canSeePackhouseView && hasPackhouseLink && (
            <>
              <div className="border-t hairline-strong mt-1 mb-1" />
              <p className="text-[10px] uppercase tracking-wider text-fg-muted px-3 py-1">
                View
              </p>
              <button onClick={() => switchMode('packhouse', `My fruit at ${packhouseLabel}`)}
                className={`w-full flex items-center gap-2 px-3 py-2.5 min-h-[44px] text-left text-sm ${
                  appMode === 'packhouse' ? 'text-fg bg-raised/60' : 'text-fg-secondary hover:bg-raised/40'
                }`}>
                <Building2 size={16} className="text-blue-400" />
                <span>My fruit at {packhouseLabel}</span>
                {appMode === 'packhouse' && <span className="ml-auto text-[10px] text-emerald-400">active</span>}
              </button>
              <button onClick={() => switchMode('farm', 'My Farm')}
                className={`w-full flex items-center gap-2 px-3 py-2.5 min-h-[44px] text-left text-sm ${
                  appMode === 'farm' ? 'text-fg bg-raised/60' : 'text-fg-secondary hover:bg-raised/40'
                }`}>
                <Sprout size={16} className="text-lime-400" />
                <span>My Farm</span>
                {appMode === 'farm' && <span className="ml-auto text-[10px] text-emerald-400">active</span>}
              </button>
            </>
          )}
        </div>
      )}
    </div>
    </>
  )
}
